/**
 * Per-component sidebar metadata: source publisher + URL, taxonomy tags, and a
 * compact summary of the input / output sockets. Used by the Toolbox info
 * panel (model_view_sidebar.tsx) to render a richer "what is this component?"
 * dropdown.
 *
 * Keys MUST match the registration name passed to `super(...)` in each
 * component class — i.e. the `BaseComponent.name` property — so the lookup
 * succeeds regardless of any `contextMenuName` aliasing.
 *
 * Components without an entry simply fall back to the existing description-
 * only view in the sidebar; missing entries are not a runtime error.
 *
 * For data-rich components (RasterBoolCatComponent, RasterComponent,
 * VectorComponent) the *actual* per-class / per-option outputs are derived
 * live from the component instance via `deriveOutputsForComponent()` rather
 * than being hard-coded here. The static `outputs` array on the entry is
 * used only as a fallback when nothing dynamic can be derived.
 */
import { BaseComponent } from "./components/base_component"

/**
 * Socket "type" the dot in the sidebar represents. These values map 1:1 to
 * the colours used for socket circles on the canvas (see model-editor.scss).
 * Keeping this enum string-based makes it easy to drive a CSS modifier class.
 */
export type SocketColor =
  | "boolean"
  | "numeric"
  | "categorical"
  | "number"
  | "property"
  | "number-or-numeric"
  | "categorical-or-boolean"
  | "table"
  | "any"

export interface ComponentSocketInfo {
  /** Rete socket key / short id (e.g. "in", "out", "mask"). */
  name: string
  color: SocketColor
  /**
   * Human-readable description shown in the Toolbox outputs/inputs panel.
   * When set, this becomes the primary line of text; `name` is shown as a
   * smaller secondary id only when it adds information (e.g. "mask" under
   * "Boolean segmentation mask").
   */
  label?: string
  /**
   * Unit string (e.g. "m", "t/ha") rendered as a small green badge to the
   * right of the socket name — matches the canvas convention where output
   * titles ending in `[[unit]]` get the same badge.
   */
  unit?: string
  /**
   * `true` when the unit is a per-area / per-something ratio (contains "/")
   * so the badge gets the slightly cooler density colour. Auto-derived from
   * `unit` when not set explicitly.
   */
  unitDensity?: boolean
  multi?: boolean
}

/** Plain-language name for each socket colour (matches canvas legend). */
export const SOCKET_COLOR_LABELS: Record<SocketColor, string> = {
  boolean: "Boolean",
  numeric: "Numeric",
  categorical: "Categorical",
  number: "Number",
  property: "Property",
  "number-or-numeric": "Number or Numeric",
  "categorical-or-boolean": "Categorical or Boolean",
  table: "Table",
  any: "Any dataset",
}

/**
 * Socket ids that are placeholders — when a `label` is present we promote
 * the label to the primary line instead of showing these generic names.
 */
const GENERIC_SOCKET_NAMES = new Set([
  "in",
  "out",
  "input",
  "output",
  "dm",
  "a",
  "b",
  "num",
  "cat_in",
  "bool_in",
  "base",
  "updates",
  "replace",
  "per option",
  "per class",
  "per habitat",
  "per category",
  "per type",
  "per year",
  "per property",
  "per dataset",
  "per extent",
  "per driver",
  "per designation",
  "per grade",
  "per depth",
  "per variable",
  "per disaster × year",
  "per crop × indicator × year",
  "per type × year",
  "height stats",
  "per variable",
  "species",
  "per disaster × year",
  "per crop × indicator × year",
])

export function isGenericSocketName(name: string): boolean {
  const n = name.trim().toLowerCase()
  if (GENERIC_SOCKET_NAMES.has(n)) return true
  return n.startsWith("per ")
}

/**
 * Primary line of text for a socket row in the Toolbox info panel.
 */
export function socketPrimaryText(socket: ComponentSocketInfo): string {
  const label = socket.label?.trim()
  const name = socket.name.trim()
  if (label && (isGenericSocketName(name) || label.length > name.length)) {
    return label
  }
  return name
}

/**
 * Optional secondary line — technical socket id when the label is primary.
 */
export function socketSecondaryText(socket: ComponentSocketInfo): string | undefined {
  const label = socket.label?.trim()
  const name = socket.name.trim()
  if (!label) return undefined
  if (socketPrimaryText(socket) !== label) return undefined
  if (name === label || isGenericSocketName(name)) return undefined
  return name
}

/**
 * Output socket display strings in the editor use a `[[unit]]` suffix to mark
 * units that the canvas renders as a small green badge — e.g. the literal
 * raster option `"Carbon stock 2003 [[t/ha]]"` becomes "Carbon stock 2003"
 * + a "t/ha" badge. Derived outputs from `RasterComponent` / `VectorComponent`
 * inherit this convention, so we mirror the canvas parser here so the
 * sidebar shows the same badge.
 *
 * Returns the cleaned text (with `[[..]]` stripped) plus an optional `unit`
 * string. `density` mirrors the canvas's "is this a per-area unit" flag and
 * drives the slightly different green shade for ratios like t/ha.
 */
export interface UnitParseResult {
  text: string
  unit?: string
  density?: boolean
}

export function parseUnitMarker(input: string): UnitParseResult {
  if (!input) return { text: input }
  // Match the *last* `[[…]]` segment — the editor convention is one trailing
  // unit suffix; using a lazy match still works but anchoring at the end is
  // cheaper and matches the canvas implementation's split-on-`[[`.
  const match = input.match(/^(.*?)\s*\[\[([^\]]+)\]\]\s*$/)
  if (!match) return { text: input.trim() }
  const text = match[1].trim()
  const unit = match[2].trim()
  return { text, unit, density: unit.includes("/") }
}

/**
 * Describes one origin for a component's data. Some components (notably
 * Digital Model) pull from several different datasets that vary by region or
 * subtype, so the sidebar can render multiple of these as a small list.
 */
export interface ComponentSourceInfo {
  /** Publisher / dataset name (e.g. "Defra National LIDAR Programme"). */
  label: string
  /** Authoritative URL to the dataset / publisher page. */
  url?: string
  /** Region scope tag — e.g. "UK", "Global", "Indonesia". */
  region?: string
  /** Optional clarifying note (e.g. "DSM, DTM, Feature Height, VOM"). */
  note?: string
}

export interface ComponentInfoEntry {
  /**
   * Long-form description shown in the sidebar info panel. Uses accessible
   * language; intended as the canonical description for users browsing the
   * Toolbox. Falls back to `BaseComponent.description` when not set.
   */
  description?: string
  /**
   * Single primary source — quick path for components with one origin.
   * Ignored when `sources` (plural) is provided.
   */
  sourceLabel?: string
  /** Authoritative URL to the dataset / publisher page. */
  sourceUrl?: string
  /**
   * Multiple sub-sources for components whose output is drawn from a family
   * of related datasets (e.g. Digital Model with Defra LIDAR + ETH canopy +
   * ASTER GDEM). When present, the sidebar renders one row per entry.
   */
  sources?: ComponentSourceInfo[]
  /** Domain tags for the data (Elevation, LULC, Biodiversity, etc.). */
  tags?: string[]
  inputs?: ComponentSocketInfo[]
  outputs?: ComponentSocketInfo[]
}

const SRC_IUCN = "https://www.iucnredlist.org/"
const SRC_GAEZ = "https://gaez.fao.org/"
const SRC_GFW = "https://www.globalforestwatch.org/"
const SRC_KEW = "https://www.kew.org/wakehurst"

export const COMPONENT_INFO: Record<string, ComponentInfoEntry> = {
  // ---------- Outputs ----------
  "Map layer": {
    description:
      "Displays the connected dataset as a styled layer on the map view. This is the main way to visualise any model output — once connected, the layer appears in the map sidebar and can be toggled on/off.",
    inputs: [
      { name: "in", color: "any", label: "Any data" },
      { name: "props", color: "property", label: "Optional Property (multi)", multi: true },
    ],
  },
  "Save dataset": {
    description:
      "Saves the connected dataset to the team library so you (or anyone on your team) can reuse it later. Once saved, the dataset appears as an option in the Load Dataset component.",
    inputs: [
      { name: "in", color: "any", label: "Any data" },
      { name: "props", color: "property", label: "Optional Property (multi)", multi: true },
    ],
  },

  // ---------- Inputs (base) ----------
  "Numeric constant": {
    description:
      "A fixed number that you type into the node (e.g. 100, 0.5). Useful as a literal value inside calculations such as Expression, Multiply or Power.",
    tags: ["Constant"],
    outputs: [{ name: "out", color: "number", label: "Number" }],
  },
  "Create Empty": {
    description:
      "Creates an empty dataset of the chosen type (Boolean, Categorical or Numeric) covering the project area. Handy as a starting point or placeholder before painting / overwriting with other components.",
    tags: ["Placeholder"],
    outputs: [
      { name: "out", color: "any", label: "Boolean / Categorical / Numeric (selectable)" },
    ],
  },
  "New Table": {
    description:
      "Creates or loads a Table dataset — tabular data with named rows and columns, not linked to the map. Use it as a lookup table to assign numeric values to categorical classes (for example scores or weights per land-cover type). Click 'Show table' on the node to edit the grid, or import and export CSV/XLSX from the toolbar. Optionally connect a Categorical dataset to the reference input to set columns from its class labels; columns stay locked while that connection is active.",
    tags: ["Placeholder", "Table"],
    inputs: [{ name: "ref", color: "categorical", label: "Reference dataset (optional)" }],
    outputs: [{ name: "out", color: "table", label: "Table" }],
  },
  "Paintbrush": {
    description:
      "Loads a layer of user-drawn annotations painted directly on the map for the active team. Each painted class becomes a separate Boolean output, plus an 'All' Categorical roll-up that combines them. [Create or Edit paintbrush layers here](/teams/{teamId}/paintbrush).",
    tags: ["User annotations"],
    outputs: [
      { name: "per class", color: "boolean", label: "One Boolean per painted class" },
      { name: "All", color: "categorical" },
    ],
  },
  "Load Dataset": {
    description:
      "Loads a previously saved dataset from your team's library (created with the Save dataset component). Each saved dataset selected here exposes its own output socket on the node.",
    tags: ["User datasets"],
    outputs: [
      { name: "per dataset", color: "any", label: "One output per saved dataset (Boolean / Categorical / Numeric)" },
    ],
  },
  "Extents": {
    description:
      "Returns named project extents as Boolean masks. Most often used to limit (clip) the rest of an analysis to a specific study area — e.g. one extent per nature reserve. [Create or Edit extents here](/teams/{teamId}/extents).",
    tags: ["Boundaries", "Study area"],
    outputs: [
      { name: "per extent", color: "boolean", label: "One Boolean per extent" },
      { name: "ALL", color: "boolean" },
    ],
  },

  // ---------- Inputs (data sources) ----------
  "ML Model Output": {
    description:
      "Sends the current map view to the WearePal AI segmentation service together with a text prompt (e.g. 'forest', 'building'). The service returns a Boolean mask of where it thinks the feature appears, plus a confidence score (0–100%) and the bounding box used.",
    sourceLabel: "WearePal AI segmentation",
    sourceUrl: "https://www.wearepal.ai/lmt.html",
    tags: ["AI", "Segmentation"],
    outputs: [
      { name: "mask", color: "boolean", label: "Segmentation mask" },
      { name: "conf", color: "numeric", label: "Confidence score (0–100%)" },
      { name: "box", color: "boolean", label: "Bounding box" },
    ],
  },
  "IUCN Rarity Weighted Richness": {
    description:
      "A global raster showing where rare species are concentrated, derived from IUCN Red List range maps. Higher values indicate areas important for rare species. Multiple years (2018–2024) and taxonomic groups (All / Amphibians / Birds / Mammals / Reptiles) are available.",
    sourceLabel: "IUCN Red List range maps (derived)",
    sourceUrl: SRC_IUCN,
    tags: ["Biodiversity", "Species richness"],
    outputs: [{ name: "per option", color: "numeric", label: "One Numeric per year × taxon" }],
  },
  "IUCN Species Richness": {
    description:
      "A global raster of how many species are present in each cell, derived from IUCN Red List range maps. Available for multiple years (2018–2024) and taxonomic groups.",
    sourceLabel: "IUCN Red List range maps (derived)",
    sourceUrl: SRC_IUCN,
    tags: ["Biodiversity", "Species richness"],
    outputs: [{ name: "per option", color: "numeric", label: "One Numeric per year × taxon" }],
  },
  "FAOSTAT Suitability Index": {
    description:
      "Shows how well-suited each part of the world is for growing a given crop (oil palm, dryland rice, wetland rice or maize) under either irrigated or rainfed conditions. Scores run from 0 (unsuitable) to 100 (highly suitable). Both 1 km (2001–2020) and 10 km (1981–2000 / 2001–2020) versions are available.",
    sourceLabel: "FAO / IIASA Global Agro-Ecological Zones (GAEZ)",
    sourceUrl: SRC_GAEZ,
    tags: ["Agriculture", "Crop suitability"],
    outputs: [{ name: "per option", color: "numeric", label: "One Numeric per crop × system × period (0–100 scale)" }],
  },
  "FAOSTAT Suitability Classiciation": {
    description:
      "Categorical version of the FAOSTAT crop-suitability data — areas are grouped into named suitability bands (e.g. Very High / High / Medium / Low / Not suitable). Outputs a Boolean mask per class plus a combined Categorical map.",
    sourceLabel: "FAO / IIASA Global Agro-Ecological Zones (GAEZ)",
    sourceUrl: SRC_GAEZ,
    tags: ["Agriculture", "Crop suitability"],
    outputs: [
      { name: "per class", color: "boolean", label: "One Boolean per suitability band" },
      { name: "All", color: "categorical" },
    ],
  },
  "CSIRO Biodiversity Habitat Index": {
    description:
      "CSIRO's Biodiversity Habitat Index — an index (0 = none, 1 = fully intact) estimating how much of an area's natural biodiversity habitat still remains. Habitat- and species-focused variants are available, with snapshot rasters for 2000–2020.",
    sourceLabel: "CSIRO BILBI programme",
    sourceUrl: "https://research.csiro.au/macroecologicalmodelling/",
    tags: ["Biodiversity", "Habitat"],
    outputs: [{ name: "per option", color: "numeric", label: "One Numeric per year × index (0–1 scale)" }],
  },
  "GLAD Crop Cover": {
    description:
      "Global Boolean maps showing where crops were being grown at four-yearly snapshots from 2003 to 2019. Produced by the University of Maryland's GLAD lab from Landsat data.",
    sourceLabel: "University of Maryland GLAD Lab (Potapov et al.)",
    sourceUrl: "https://glad.umd.edu/dataset/croplands",
    tags: ["LULC", "Agriculture"],
    outputs: [{ name: "per year", color: "boolean", label: "One Boolean per snapshot year (2003–2019)" }],
  },
  "SPAM": {
    description:
      "IFPRI's Spatial Production Allocation Model (MapSPAM) — global maps of crop production, harvested area, physical area and yield broken down by crop × farming method (irrigated / rainfed / subsistence) for the years 2000, 2010 and 2020.",
    sourceLabel: "IFPRI MapSPAM",
    sourceUrl: "https://mapspam.info/",
    tags: ["Agriculture", "Crop production", "Yield"],
    outputs: [{ name: "per option", color: "numeric", label: "One Numeric per crop × indicator × system × year" }],
  },
  "NASA FIRMS Fires": {
    description:
      "Live NASA FIRMS active-fire hotspots from MODIS and VIIRS satellites for the project area. Set a date range (GMT, up to 90 days), optionally a sensor, and a radius as an area in km² (0 = one grid cell; larger values expand each hotspot to a circle of that area). Outputs fire presence, detection count per cell, and the highest Fire Radiative Power (MW) where detections overlap. Detections are hotspots, not mapped fire perimeters.",
    sourceLabel: "NASA FIRMS (LANCE / EOSDIS)",
    sourceUrl: "https://firms.modaps.eosdis.nasa.gov/",
    tags: ["Fire", "Satellite", "Near real-time"],
    outputs: [
      { name: "presence", color: "boolean", label: "Fire presence" },
      { name: "count", color: "numeric", label: "Fire count" },
      { name: "frp", color: "numeric", label: "Fire Radiative Power", unit: "MW" },
    ],
  },
  "Digital Model": {
    description:
      "A family of elevation, terrain and canopy-height rasters drawn from several different surveys. Pick the model you want from the dropdown — the available options depend on the project's enabled regions (UK / Global / Indonesia) and team permissions. All values are in metres.",
    sources: [
      {
        label: "Defra / Environment Agency — National LIDAR Programme",
        note: "1 m and 2 m Digital Surface Model, Digital Terrain Model (elevation + hillshade), First-Return DSM, Vegetation Object Model",
        url: "https://environment.data.gov.uk/survey",
        region: "UK",
      },
      {
        label: "Royal Botanic Gardens, Kew — Wakehurst LIDAR",
        note: "DSM 2 m, DTM 5 m, Feature Height (legacy lidar:* layers — KewLidar permission)",
        url: SRC_KEW,
        region: "UK",
      },
      {
        label: "ETH Zurich — Sentinel-2 global canopy height (Lang et al.)",
        note: "Canopy top-height and standard deviation rasters, 10 m resolution",
        url: "https://langnico.github.io/globalcanopyheight/",
        region: "Global",
      },
      {
        label: "NASA / METI — ASTER Global DEM",
        note: "ASTER GDEM for Indonesia",
        url: "https://asterweb.jpl.nasa.gov/gdem.asp",
        region: "Indonesia",
      },
      {
        label: "DEMNAS — Gorontalo",
        note: "DEMNAS elevation and precomputed slope for Gorontalo",
        region: "Indonesia",
      },
    ],
    tags: ["Elevation", "Terrain", "Canopy height", "LiDAR", "Slope"],
    outputs: [{ name: "dm", color: "numeric", label: "Elevation / height (m) or slope (degrees), depending on model" }],
  },
  "OpenStreetMap Land Use": {
    description:
      "Pulls selected land-use categories (e.g. forest, residential, farmland) live from OpenStreetMap via the Overpass API. Prefer Preset Categories for common types. For tags not in that list, set Build Query / osmCustomQueries to [{key, value, type}] (type is node|way|relation|all). Returns a single Boolean layer that is true wherever any selected category or custom query was found.",
    sourceLabel: "OpenStreetMap contributors",
    sourceUrl: "https://wiki.openstreetmap.org/wiki/Key:landuse",
    tags: ["LULC", "Land use"],
    outputs: [{ name: "out", color: "boolean" }],
  },
  "OpenStreetMap Place": {
    description:
      "Looks up a named place such as Jakarta and returns a Boolean layer that is true inside that place's OpenStreetMap boundary. A place that is only a point, such as a town or village, is marked with Nominatim's estimated extent. Set Place to the name. When several matches come back, Match lists them; leave it unset to use the best administrative boundary. The project area has to cover the place.",
    sourceLabel: "OpenStreetMap contributors",
    sourceUrl: "https://wiki.openstreetmap.org/wiki/Key:place",
    tags: ["Places", "Boundaries"],
    outputs: [{ name: "out", color: "boolean" }],
  },
  "ISRIC Soil Data": {
    description:
      "Soil-property layers fetched live from the ISRIC SoilGrids API: clay / sand / silt content, organic carbon, bulk density, total nitrogen, pH, CEC and more — each available at several soil-depth bands (0–5 cm, 5–15 cm, etc.).",
    sourceLabel: "ISRIC – World Soil Information (SoilGrids)",
    sourceUrl: "https://www.isric.org/",
    tags: ["Soil", "Soil properties"],
    outputs: [{ name: "per property", color: "numeric", label: "One Numeric per property × depth" }],
  },

  // ---------- Inputs (UK datasets) ----------
  "UKCEH Land Cover": {
    description:
      "The UK Centre for Ecology & Hydrology's Land Cover Map for the UK at 10 m resolution. You pick a year (2021 / 2022 / 2023); the component outputs a Boolean mask for each habitat (e.g. Broadleaved Woodland, Arable, Saltmarsh) plus a combined Categorical map.",
    sourceLabel: "UK Centre for Ecology & Hydrology",
    sourceUrl: "https://www.ceh.ac.uk/data/ukceh-land-cover-maps",
    tags: ["LULC", "Habitat"],
    outputs: [
      { name: "per habitat", color: "boolean", label: "One Boolean per habitat" },
      { name: "All", color: "categorical" },
    ],
  },
  "Living England Land Cover": {
    description:
      "Natural England's Living England habitat probability map — an England-wide habitat classification built from satellite imagery and field survey. Outputs one Boolean mask per habitat type.",
    sourceLabel: "Natural England — Living England programme",
    sourceUrl: "https://naturalengland.blog.gov.uk/2022/04/05/living-england-from-satellite-imagery-to-a-national-scale-habitat-map/",
    tags: ["LULC", "Habitat"],
    outputs: [{ name: "per habitat", color: "boolean", label: "One Boolean per habitat" }],
  },
  "Non-woodland trees": {
    description:
      "Forest Research's Trees Outside Woodland (TOW v1) dataset — maps trees that sit outside formal woodland (in hedgerows, gardens, parks, etc.) across the English regions. Outputs Boolean masks per category (Lone trees / Group of Trees / Small Woodland / NFI OHC) and per-cell tree-height statistics (max, min, mean, std dev).",
    sourceLabel: "Forest Research — Trees Outside Woodland (TOW)",
    sourceUrl: "https://www.forestresearch.gov.uk/tools-and-resources/fthr/tree-cover-outside-woodland-in-great-britain/",
    tags: ["Trees", "Canopy height", "Forestry"],
    outputs: [
      { name: "per category", color: "boolean", label: "Lone trees / Group / Small Woodland / NFI OHC" },
      { name: "height stats", color: "numeric", label: "Max / Min / Mean / Std dev" },
    ],
  },
  "National Forest Inventory": {
    description:
      "Forest Research's National Forest Inventory of Great Britain — a periodic woodland census. Provides Boolean masks for each woodland category (e.g. broadleaved / conifer / felled) and an Interpreted Forest Type (IFT_IOA) Categorical map.",
    sourceLabel: "Forest Research — National Forest Inventory GB",
    sourceUrl: "https://www.forestresearch.gov.uk/tools-and-resources/fthr/national-forest-inventory/",
    tags: ["Forestry", "LULC"],
    outputs: [
      { name: "per category", color: "boolean" },
      { name: "Category / IFT_IOA", color: "categorical" },
    ],
  },
  "ALC Grade": {
    description:
      "Natural England's Agricultural Land Classification (ALC) — grades land in England from Grade 1 (best agricultural quality) to Grade 5 (poorest) with sub-grades 3a/3b for moderate land. Outputs one Boolean mask per grade.",
    sourceLabel: "Natural England — Agricultural Land Classification",
    sourceUrl: "https://naturalengland-defra.opendata.arcgis.com/datasets/Defra::provisional-agricultural-land-classification-alc-grades/about",
    tags: ["Agriculture", "Land classification"],
    outputs: [{ name: "per grade", color: "boolean" }],
  },
  "Indices of Multiple Deprivation": {
    description:
      "The UK government's official measure of relative deprivation for England (IMD 2019). The overall IMD index plus seven sub-domains (Income, Employment, Education, Health, Crime, Barriers to Housing & Services, Living Environment) are available as either ranks or deciles.",
    sourceLabel: "UK MHCLG — English Indices of Deprivation 2019 (via CDRC)",
    sourceUrl: "https://data.cdrc.ac.uk/dataset/index-multiple-deprivation-imd",
    tags: ["Socioeconomic", "Deprivation"],
    outputs: [{ name: "per option", color: "numeric", label: "Rank or decile per IMD domain" }],
  },
  "Recorded species": {
    description:
      "Counts NBN Atlas wildlife sightings of a chosen species or family within a date range across the project area. Records are spatially blurred (~1 km) to protect sensitive species locations.",
    sourceLabel: "NBN Atlas (National Biodiversity Network)",
    sourceUrl: "https://nbnatlas.org/",
    tags: ["Biodiversity", "Species records"],
    outputs: [{ name: "out", color: "numeric", label: "Count of sightings" }],
  },
  "NEVO": {
    description:
      "Outputs from the University of Exeter's NEVO model — values for agriculture, forestry and recreation indicators (and many sub-indicators such as expected farm profits or carbon sequestration) on a 2 km grid covering the UK.",
    sourceLabel: "University of Exeter LEEP — NEVO",
    sourceUrl: "https://www.leep.exeter.ac.uk/nevo/documentation/",
    tags: ["Ecosystem services", "Valuation"],
    outputs: [{ name: "out", color: "numeric", label: "Selected NEVO variable" }],
  },
  "ORVal": {
    description:
      "Outputs from the University of Exeter's Outdoor Recreation Valuation Tool — estimates the monetary value of outdoor recreation across England (welfare value per visit / per area).",
    sourceLabel: "University of Exeter LEEP — ORVal",
    sourceUrl: "https://www.leep.exeter.ac.uk/orval/",
    tags: ["Recreation", "Valuation", "Ecosystem services"],
    outputs: [{ name: "per option", color: "numeric", label: "One output per ORVal indicator" }],
  },
  "UK Census 2021": {
    description:
      "Variables from the UK 2021 Census — e.g. age groups, ethnic group, qualifications, household composition, deprivation. Each variable is rendered as a percentage raster (0–100%).",
    sourceLabel: "Office for National Statistics (ONS) — Census 2021",
    sourceUrl: "https://www.ons.gov.uk/census",
    tags: ["Socioeconomic", "Demographics"],
    outputs: [{ name: "per option", color: "numeric", label: "Percentage (0–100)" }],
  },
  "OS Green Spaces": {
    description:
      "Ordnance Survey's Open Greenspace dataset — public parks and gardens / playing fields / allotments / cemeteries / etc. Outputs one Boolean mask per type of green space.",
    sourceLabel: "Ordnance Survey — OS Open Greenspace",
    sourceUrl: "https://www.ordnancesurvey.co.uk/products/os-open-greenspace",
    tags: ["LULC", "Green infrastructure"],
    outputs: [{ name: "per type", color: "boolean" }],
  },
  "CROME 2021": {
    description:
      "Defra's Crop Map of England 2021 — about 15 crop categories (wheat, barley, OSR, maize, etc.) plus grassland, woodland, water and other non-agricultural covers. Outputs a Boolean mask per category plus combined Categorical maps.",
    sourceLabel: "Defra / Rural Payments Agency — Crop Map of England 2021",
    sourceUrl: "https://www.data.gov.uk/dataset/aaedb588-fc86-498f-acab-5fa1b261fdd5/crop-map-of-england-crome-2021",
    tags: ["Agriculture", "LULC"],
    outputs: [
      { name: "per category", color: "boolean" },
      { name: "All / All (Original)", color: "categorical" },
    ],
  },
  "Ancient Tree Inventory": {
    description:
      "Woodland Trust's Ancient Tree Inventory — citizen-recorded sightings of ancient and notable trees across the UK. Records are aggregated to the project area and split out by tree category.",
    sourceLabel: "The Woodland Trust — Ancient Tree Inventory",
    sourceUrl: "https://ati.woodlandtrust.org.uk/",
    tags: ["Trees", "Heritage", "Biodiversity"],
    outputs: [{ name: "per category", color: "boolean" }],
  },
  "Designations": {
    description:
      "Statutory environmental designations in England — Sites of Special Scientific Interest (SSSI), Special Areas of Conservation (SAC), Special Protection Areas (SPA), Ramsar, National Parks, Areas of Outstanding Natural Beauty (AONB), Local Nature Reserves and more. One Boolean mask per designation.",
    sourceLabel: "Natural England statutory designations; JNCC international sites",
    sourceUrl: "https://naturalengland-defra.opendata.arcgis.com/",
    tags: ["Conservation", "Protected areas"],
    outputs: [{ name: "per designation", color: "boolean" }],
  },
  "Hedgerows": {
    description:
      "Defra's Land Cover Map hedgerow layer — a Boolean mask of where hedgerows occur within the project area. Useful for connectivity and landscape-structure analyses.",
    sourceLabel: "Defra — Land Cover Map hedgerow layer",
    sourceUrl: "https://www.ceh.ac.uk/data/ukceh-land-cover-maps",
    tags: ["LULC", "Field boundaries", "Linear features"],
    outputs: [{ name: "out", color: "boolean", label: "Hedgerows" }],
  },

  // ---------- Inputs (Kew Wakehurst) ----------
  "Kew Samples": {
    description:
      "Field soil sample point data collected at Kew's Wakehurst estate (Sussex) — rendered as a numeric raster. One output per measured soil property.",
    sourceLabel: "Royal Botanic Gardens, Kew — Wakehurst soil survey",
    sourceUrl: SRC_KEW,
    tags: ["Soil", "Soil properties"],
    outputs: [{ name: "per property", color: "numeric" }],
  },
  "Wakehurst Trees": {
    description:
      "Tree-crown vector data from Kew's Wakehurst estate — distinguishes broadleaf (deciduous) crowns, conifer crowns and full canopy from QSM (Quantitative Structure Modelling) lidar surveys.",
    sourceLabel: "Royal Botanic Gardens, Kew — Wakehurst tree survey",
    sourceUrl: SRC_KEW,
    tags: ["Trees", "Canopy"],
    outputs: [{ name: "per category", color: "any", label: "Broadleaf / Conifer crowns and full canopy" }],
  },
  "Wakehurst Soil Carbon": {
    description:
      "Modelled soil-carbon stocks (tonnes per hectare) for Kew's Wakehurst estate at four soil-depth intervals (0–15, 0–30, 15–30, 30–45 cm).",
    sourceLabel: "Royal Botanic Gardens, Kew — Wakehurst soil-carbon modelling",
    sourceUrl: SRC_KEW,
    tags: ["Soil", "Carbon"],
    outputs: [{ name: "per depth", color: "numeric", label: "t/ha at 0–15, 0–30, 15–30, 30–45 cm" }],
  },
  "NATMAP Soil": {
    description:
      "Cranfield University's NATMAPcarbon soil-carbon stock estimates for the UK — minimum, maximum and mean values across three depth bands (0–10 cm, 0–15 cm, 0–30 cm), in kg/m².",
    sourceLabel: "Cranfield University — NATMAPcarbon (LandIS)",
    sourceUrl: "https://www.landis.org.uk/data/natmap_carbon.cfm",
    tags: ["Soil", "Carbon"],
    outputs: [{ name: "per option", color: "numeric", label: "Min / Max / Mean per depth band (kg/m²)" }],
  },

  // ---------- Inputs (Indonesia & Gorontalo) ----------
  "GBIF Indonesia Species Occurrences": {
    description:
      "Counts unique bird species recorded in each grid cell from GBIF (Global Biodiversity Information Facility) occurrence data for Indonesia. You can configure the grid resolution (zoom) and the minimum number of occurrences a species needs to be counted.",
    sourceLabel: "GBIF (Global Biodiversity Information Facility)",
    sourceUrl: "https://www.gbif.org/occurrence/download/0004740-250515123054153/",
    tags: ["Biodiversity", "Species records"],
    inputs: [
      { name: "n", color: "number", label: "Minimum occurrences" },
      { name: "zoom", color: "number", label: "Resolution / zoom level" },
    ],
    outputs: [{ name: "species", color: "numeric", label: "Unique species per cell" }],
  },
  "MapBiomas": {
    description:
      "Annual land-cover classification for Indonesia from the MapBiomas Indonesia initiative — categories range from primary forest through plantations to urban. Outputs per-class Boolean masks plus a combined Categorical map.",
    sourceLabel: "MapBiomas Indonesia initiative",
    sourceUrl: "https://indonesia.mapbiomas.org/",
    tags: ["LULC"],
    outputs: [
      { name: "per class", color: "boolean" },
      { name: "All", color: "categorical" },
    ],
  },
  "Indonesia Carbon Stock": {
    description:
      "Vector layer of above-ground carbon stocks (tonnes per hectare) across Indonesia at several snapshot years (2003–2023).",
    tags: ["Carbon"],
    outputs: [{ name: "per year", color: "numeric", label: "t/ha" }],
  },
  "Indonesia Designations": {
    description:
      "Two Indonesia conservation/hydrology layers: the Key Biodiversity Areas (KBA 2023) and the network of major rivers. Each as a Boolean mask.",
    sourceLabel: "Key Biodiversity Areas Partnership (KBA)",
    sourceUrl: "https://www.keybiodiversityareas.org/",
    tags: ["Conservation", "Protected areas", "Hydrology"],
    outputs: [
      { name: "KBA", color: "boolean" },
      { name: "Rivers", color: "boolean" },
    ],
  },
  "Land Layout": {
    description:
      "Indonesian national land-use classification raster at 10 m resolution. Outputs per-class Boolean masks plus a combined Categorical map.",
    tags: ["LULC"],
    outputs: [
      { name: "per class", color: "boolean" },
      { name: "All", color: "categorical" },
    ],
  },
  "Forest Type": {
    description:
      "Indonesia forest-type vector data for 2010 and 2020 snapshots — outputs per forest-type × year (e.g. primary dryland forest 2010 vs 2020) plus a Categorical map per snapshot.",
    tags: ["Forestry", "LULC"],
    outputs: [
      { name: "per type × year", color: "boolean" },
      { name: "Forest Type (per snapshot)", color: "categorical" },
    ],
  },
  "Forest Type (2)": {
    description:
      "Indonesia forest vs. non-forest categorical raster (Hutan). Outputs per-class Boolean masks plus a combined Categorical map.",
    tags: ["Forestry", "LULC"],
    outputs: [
      { name: "per class", color: "boolean" },
      { name: "All", color: "categorical" },
    ],
  },
  "GFW Indonesia Forest 2000-2020": {
    description:
      "Forest extent, canopy height and net forest gain/loss rasters for Indonesia for 2000 and 2020, sourced from Global Forest Watch (built on the Hansen and Potapov University of Maryland datasets).",
    sourceLabel: "Global Forest Watch (Hansen / Potapov UMD)",
    sourceUrl: SRC_GFW,
    tags: ["Forestry", "Forest change", "Canopy height"],
    outputs: [
      { name: "Forest Extent 2000 / 2020", color: "boolean" },
      { name: "Forest Height 2000 / 2020", color: "numeric", label: "metres" },
      { name: "Net Gain / Loss", color: "numeric", label: "metres" },
    ],
  },
  "Indonesia Forest Loss Drivers": {
    description:
      "Maps the dominant driver of forest loss in Indonesia for each pixel where loss occurred (2001–2024) — e.g. commodity-driven deforestation, shifting agriculture, urbanisation. Outputs per-driver Boolean masks plus a combined Categorical map.",
    tags: ["Forestry", "Forest change"],
    outputs: [
      { name: "per driver", color: "boolean" },
      { name: "All", color: "categorical" },
    ],
  },
  "Indonesia Forest Loss by Year": {
    description:
      "Hansen Global Forest Change loss-year raster for Indonesia (tile 10N 120E) — shows the year in which each pixel of forest was lost. Outputs a Boolean mask per year plus a combined Categorical map.",
    sourceLabel: "Global Forest Watch — Hansen GFC v1.12",
    sourceUrl: SRC_GFW,
    tags: ["Forestry", "Forest change"],
    outputs: [
      { name: "per year", color: "boolean" },
      { name: "All", color: "categorical" },
    ],
  },
  "Indonesia Disaster per Regency": {
    description:
      "Counts the number of natural disasters recorded in each Indonesian regency between two selectable years. Outputs one Numeric layer per disaster type (Floods, Landslides, Forest fires).",
    tags: ["Hazards", "Disasters"],
    inputs: [
      { name: "YearFrom", color: "number" },
      { name: "YearTo", color: "number" },
    ],
    outputs: [
      { name: "Floods", color: "numeric" },
      { name: "Landslides", color: "numeric" },
      { name: "Forest fires", color: "numeric" },
    ],
  },
  "Indonesia Rice Yield": {
    description:
      "Annual rice-yield (Beras) data for Indonesia by administrative area for the years 2018–2024.",
    tags: ["Agriculture", "Yield"],
    outputs: [{ name: "per year", color: "numeric", label: "Rice yield 2018–2024" }],
  },
  "LGRIP 2015": {
    description:
      "NASA's Landsat-derived Global Rainfed and Irrigated-cropland Product for 2015 — classifies every pixel as one of: Water, Non-cropland, Irrigated cropland, Rainfed cropland.",
    sourceLabel: "NASA / USGS — LGRIP 2015 (Teluguntla et al.)",
    sourceUrl: "https://lpdaac.usgs.gov/products/lgrip30v001/",
    tags: ["LULC", "Agriculture"],
    outputs: [
      { name: "per class", color: "boolean", label: "Water / Non-crop / Irrigated / Rainfed" },
      { name: "All", color: "categorical" },
    ],
  },
  "Disaster per District": {
    description:
      "Indonesian natural-disaster counts at district (kabupaten) resolution broken down by disaster type and year. Outputs one Numeric layer per disaster × year combination.",
    tags: ["Hazards", "Disasters"],
    outputs: [{ name: "per disaster × year", color: "numeric" }],
  },
  "BPS Gorontalo Crops": {
    description:
      "Crop statistics from Statistics Indonesia (BPS) for Gorontalo province — production (tonnes), yield (t/ha) and harvested area per crop per year.",
    sourceLabel: "BPS – Statistics Indonesia (Gorontalo Province)",
    sourceUrl: "https://gorontalo.bps.go.id/",
    tags: ["Agriculture", "Statistics", "Yield"],
    outputs: [{ name: "per crop × indicator × year", color: "numeric" }],
  },
  "Lempake LULC": {
    description:
      "Land-use and land-cover polygons for Lempake in East Kalimantan, with snapshots for 2023 and 2025. Choose a classification — detailed land use (PL) or the coarser IKLH grouping — and one or both years. Outputs a Boolean mask for each class plus a combined Categorical map.",
    sourceLabel: "GeoServer — ind:lulc_lempake",
    tags: ["LULC"],
    outputs: [
      { name: "per class", color: "boolean", label: "One Boolean per land-use class for the selected year and classification" },
      { name: "combined", color: "categorical", label: "All classes of the selected classification and year" },
    ],
  },
  "Gorontalo Soils": {
    description:
      "Soil and land-system polygons for three Gorontalo villages (Dulamayo Selatan, Modelidu and Tupa). Pick an attribute to expose a Boolean mask for every class plus a combined Categorical map: USDA soil order (Entisol, Ultisol), land system (Bakunan, Bukit Balang, Telawi), USDA taxonomy, texture, lithology, rainfall range or landform description.",
    sourceLabel: "GeoServer — ind:soils (three-village soil map)",
    tags: ["Soil", "Land systems"],
    outputs: [
      { name: "per class", color: "boolean", label: "One Boolean per class of the selected attribute" },
      { name: "combined", color: "categorical", label: "All classes of the selected attribute" },
    ],
  },

  // ---------- Conversions ----------
  "Number to numeric dataset": {
    description:
      "Converts a single Number into a Numeric dataset that has the same constant value in every cell across the project area.",
    inputs: [{ name: "in", color: "number" }],
    outputs: [{ name: "out", color: "numeric" }],
  },
  "Numeric dataset to number": {
    description:
      "Reduces a Numeric dataset to a single Number (typically a summary statistic such as a total or mean).",
    inputs: [{ name: "in", color: "numeric" }],
    outputs: [{ name: "out", color: "number" }],
  },
  "Boolean to categorical dataset": {
    description:
      "Combines several Boolean (true/false) datasets into a single Categorical dataset — each Boolean input becomes one class in the output.",
    inputs: [{ name: "in", color: "boolean", multi: true }],
    outputs: [{ name: "out", color: "categorical" }],
  },
  "Categorical to boolean datasets": {
    description:
      "Splits a Categorical dataset into separate Boolean (true/false) outputs — one output per category in the input.",
    inputs: [{ name: "in", color: "categorical" }],
    outputs: [{ name: "per class", color: "boolean" }],
  },
  "Categorical to numeric dataset": {
    description:
      "Assigns a number to each category in a Categorical dataset and combines them into a Numeric dataset. Per-class values can be wired in as Number inputs, or entered as a static lookup on the node.",
    inputs: [
      { name: "in", color: "categorical" },
      { name: "catVal_<value>", color: "number", label: "One Number per class (optional)", multi: true },
    ],
    outputs: [{ name: "out", color: "numeric" }],
  },
  "Rename": {
    description:
      "Passes the input through unchanged but with a user-supplied label — useful for tidying up output names in the model.",
    inputs: [{ name: "in", color: "any" }],
    outputs: [{ name: "out", color: "any", label: "Same type as input" }],
  },

  // ---------- Calculations ----------
  "Area": {
    description:
      "Calculates the total area covered by the Boolean input across the project extent. Result is in square kilometres.",
    inputs: [{ name: "in", color: "boolean" }],
    outputs: [{ name: "out", color: "number", label: "Area (km²)" }],
  },
  "Distance Map": {
    description:
      "For every cell, calculates the straight-line (Euclidean) distance in metres to the nearest 'true' cell of the input Boolean. Useful for proximity / accessibility analyses.",
    inputs: [{ name: "in", color: "boolean" }],
    outputs: [{ name: "out", color: "numeric", label: "Distance (m)" }],
  },
  "Cell area in": {
    description:
      "Returns the ground area of a single cell of the input dataset's tile grid. Used to convert per-pixel quantities into real-world areas.",
    inputs: [{ name: "in", color: "any" }],
    outputs: [{ name: "out", color: "number" }],
  },
  "Interpolation": {
    description:
      "Inverse-distance-weighted (IDW) interpolation — fills in gaps in a sparse Numeric dataset by averaging nearby values. You can configure the maximum search distance, the IDW power (how strongly nearby points dominate), and how many neighbours to use.",
    inputs: [
      { name: "input", color: "numeric" },
      { name: "maxdist", color: "number" },
      { name: "p", color: "number", label: "IDW power" },
      { name: "closest_points", color: "number" },
    ],
    outputs: [{ name: "output", color: "numeric" }],
  },
  "Slope": {
    description:
      "Calculates terrain slope and aspect from an elevation model at the input's cell size. Slope outputs: Horn/Ritter in degrees or percent. Aspect outputs: Horn/Ritter in degrees (0 = North, clockwise to 360; flat cells are empty). Horn uses weighted 8-neighbours; Ritter uses 4 cardinal neighbours.",
    inputs: [
      { name: "input", color: "numeric", label: "Elevation model" },
    ],
    outputs: [
      { name: "horn_deg", color: "numeric", label: "Slope Horn [[degrees]]" },
      { name: "horn_pct", color: "numeric", label: "Slope Horn [[%]]" },
      { name: "ritter_deg", color: "numeric", label: "Slope Ritter [[degrees]]" },
      { name: "ritter_pct", color: "numeric", label: "Slope Ritter [[%]]" },
      { name: "horn_aspect", color: "numeric", label: "Aspect Horn [[degrees]]" },
      { name: "ritter_aspect", color: "numeric", label: "Aspect Ritter [[degrees]]" },
    ],
  },

  // ---------- Charts ----------
  "Bar chart": {
    description:
      "Draws a small bar chart on the node from the Number inputs you connect — handy for quick visual comparisons of summary statistics inside the model. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [{ name: "in", color: "number", multi: true }],
  },
  "Pie chart": {
    description:
      "Draws the snapshot pie chart for every cell in a Categorical or Boolean dataset, rather than a map snapshot. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [{ name: "in", color: "categorical-or-boolean", label: "Categorical or Boolean" }],
  },
  "Bar distribution chart": {
    description:
      "Draws the snapshot bar distribution for every cell in a Categorical or Boolean dataset, rather than a map snapshot. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [{ name: "in", color: "categorical-or-boolean", label: "Categorical or Boolean" }],
  },
  "Histogram": {
    description:
      "Draws the snapshot histogram for every cell in a Numeric dataset, rather than a map snapshot. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [{ name: "in", color: "numeric", label: "Numeric dataset" }],
  },
  "Table chart": {
    description:
      "Draws the snapshot table chart for every cell in a Categorical dataset, using a connected Table whose columns match the category labels. Each bar is the area-weighted mean of one table row. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [
      { name: "categories", color: "categorical", label: "Categorical dataset" },
      { name: "table", color: "table", label: "Table" },
    ],
  },
  "Table compare chart": {
    description:
      "Compare version of the table chart. Connect categorical datasets A and B plus a Table whose columns match the category labels. Each bar is the change in that row’s area-weighted mean from A to B (A − B). Swap the arrow to show B → A. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [
      { name: "a", color: "categorical", label: "Categorical dataset A" },
      { name: "b", color: "categorical", label: "Categorical dataset B" },
      { name: "table", color: "table", label: "Table" },
    ],
  },
  "Spider diagram": {
    description:
      "Draws the snapshot spider diagram for every cell in one or more Categorical datasets, using a connected Table whose columns match the category labels. Each dataset is a polygon of that table’s area-weighted row means. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [
      { name: "categories", color: "categorical", label: "Categorical datasets", multi: true },
      { name: "table", color: "table", label: "Table" },
    ],
  },
  "Sankey diagram": {
    description:
      "Draws a Sankey diagram on the node visualising the flows between two or more Categorical datasets — for example showing how cells move between land-cover classes over time. Use the eye icon to expose the chart in the Detail panel.",
    inputs: [{ name: "in", color: "categorical", multi: true }],
  },

  // ---------- Set operations ----------
  "Union": {
    description: "Boolean OR (⋃) — outputs true wherever any of the input layers is true.",
    inputs: [{ name: "in", color: "boolean", multi: true }],
    outputs: [{ name: "out", color: "boolean", label: "∪ Inputs" }],
  },
  "Intersection": {
    description: "Boolean AND (⋂) — outputs true only where every input layer is true.",
    inputs: [{ name: "in", color: "boolean", multi: true }],
    outputs: [{ name: "out", color: "boolean", label: "∩ Inputs" }],
  },
  "Categorical intersect": {
    description:
      "Keeps the Categorical input only where the Boolean mask is true; everywhere else becomes unset (no value).",
    inputs: [
      { name: "cat_in", color: "categorical" },
      { name: "bool_in", color: "boolean" },
    ],
    outputs: [{ name: "out", color: "categorical" }],
  },
  "Categorical merge": {
    description:
      "Overlays one Categorical dataset ('updates') on top of another ('base'). The result is a merged Categorical map plus a Boolean split per class — useful for layering changes onto a baseline land-cover map.",
    inputs: [
      { name: "base", color: "categorical" },
      { name: "updates", color: "categorical" },
    ],
    outputs: [
      { name: "All", color: "categorical" },
      { name: "per class", color: "boolean" },
    ],
  },
  "Categorical difference": {
    description:
      "Compares two Categorical datasets (A and B). Outputs B's category only where the cell differs from A, a Boolean mask over all changed cells, and a Boolean output per class in B for cells that changed to that category.",
    inputs: [
      { name: "a", color: "categorical", label: "A" },
      { name: "b", color: "categorical", label: "B" },
    ],
    outputs: [
      { name: "out", color: "categorical", label: "Changed" },
      { name: "All", color: "boolean", label: "Mask" },
      { name: "per class", color: "boolean" },
    ],
  },
  "Payoff matrix": {
    description:
      "Cross-tabulates two Categorical / Boolean datasets and assigns a numeric payoff to each combination of classes via an editable matrix on the node. Outputs the resulting Numeric layer.",
    inputs: [
      { name: "a", color: "categorical-or-boolean" },
      { name: "b", color: "categorical-or-boolean" },
    ],
    outputs: [{ name: "out", color: "numeric", label: "Payoff" }],
  },
  "Set difference": {
    description: "Boolean A minus B — true only in cells where A is true and B is false.",
    inputs: [
      { name: "a", color: "boolean" },
      { name: "b", color: "boolean" },
    ],
    outputs: [{ name: "out", color: "boolean", label: "A − B" }],
  },
  "Symmetric difference": {
    description:
      "Boolean exclusive-OR (Δ) — true in cells where an odd number of inputs are true.",
    inputs: [{ name: "in", color: "boolean", multi: true }],
    outputs: [{ name: "out", color: "boolean", label: "Δ Inputs" }],
  },
  "Complement": {
    description: "Boolean inversion (postfix ′) — true wherever the input is false and vice versa.",
    inputs: [{ name: "a", color: "boolean" }],
    outputs: [{ name: "out", color: "boolean", label: "A′" }],
  },

  // ---------- Arithmetic ----------
  "Mask numeric data": {
    description:
      "Masks a Numeric dataset by a Boolean dataset — cells outside the mask become unset (NaN). Useful for restricting an analysis to a study area.",
    inputs: [
      { name: "num", color: "number-or-numeric" },
      { name: "mask", color: "boolean" },
    ],
    outputs: [{ name: "out", color: "numeric" }],
  },
  "Expression": {
    description:
      "Lets you type a maths formula (e.g. (a + b) * 0.5) and evaluates it pixel-by-pixel over the named inputs. Custom expressions can be saved and reused by your team, and one input is generated per variable used in the formula. [Create or Edit expressions here](/teams/{teamId}/expressions).",
    inputs: [{ name: "per variable", color: "number-or-numeric", label: "One input per symbol used" }],
    outputs: [{ name: "out", color: "number-or-numeric" }],
  },
  "Log": {
    description: "Logarithm of the input. Base defaults to natural log (ln) if no base is supplied.",
    inputs: [
      { name: "input", color: "numeric" },
      { name: "base", color: "number", label: "Default natural log" },
    ],
    outputs: [{ name: "output", color: "numeric" }],
  },
  "Min": {
    description: "Element-wise minimum of A and B — keeps the smaller value in each cell.",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "Min(A, B)" }],
  },
  "Max": {
    description: "Element-wise maximum of A and B — keeps the larger value in each cell.",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "Max(A, B)" }],
  },
  "Clip": {
    description:
      "Clips (clamps) a Numeric dataset to a user-defined value range. Values below the minimum become the minimum, values above the maximum become the maximum.",
    inputs: [{ name: "in", color: "numeric" }],
    outputs: [{ name: "out", color: "numeric", label: "Clipped" }],
  },
  "Sum": {
    description: "Adds all input layers together (∑ Aᵢ) cell by cell.",
    inputs: [{ name: "in", color: "numeric", multi: true }],
    outputs: [{ name: "out", color: "numeric", label: "∑ Inputs" }],
  },
  "Merge": {
    description:
      "Merges multiple Numeric datasets into one — the first non-NaN value in each cell wins. Useful for combining partial-coverage layers into a seamless map.",
    inputs: [{ name: "in", color: "numeric", multi: true }],
    outputs: [{ name: "out", color: "numeric", label: "Merged (first non-NaN wins)" }],
  },
  "Product": {
    description: "Multiplies all input layers together (∏ Aᵢ) cell by cell.",
    inputs: [{ name: "in", color: "numeric", multi: true }],
    outputs: [{ name: "out", color: "numeric", label: "∏ Inputs" }],
  },
  "Add": {
    description: "Adds two layers together cell by cell (A + B).",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "A + B" }],
  },
  "Subtract": {
    description: "Subtracts B from A cell by cell (A − B).",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "A − B" }],
  },
  "Multiply": {
    description: "Multiplies two layers together cell by cell (A × B).",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "A × B" }],
  },
  "Divide": {
    description: "Divides A by B cell by cell (A ÷ B). Cells where B = 0 become NaN.",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "A ÷ B" }],
  },
  "Power": {
    description: "Raises A to the power of B cell by cell (A^B).",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "number-or-numeric", label: "A^B" }],
  },
  "Negate": {
    description: "Negates the dataset — multiplies every value by −1.",
    inputs: [{ name: "a", color: "numeric" }],
    outputs: [{ name: "out", color: "numeric", label: "−A" }],
  },
  "Reciprocal": {
    description: "Reciprocal (1 / A; postfix ⁻¹) of the dataset cell by cell.",
    inputs: [{ name: "a", color: "numeric" }],
    outputs: [{ name: "out", color: "numeric", label: "A⁻¹" }],
  },
  "Less": {
    description:
      "Element-wise less-than comparison — outputs a Boolean dataset that is true wherever A < B. Enable “And equal to” for A ≤ B.",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "boolean", label: "A < B" }],
  },
  "Greater": {
    description:
      "Element-wise greater-than comparison — outputs a Boolean dataset that is true wherever A > B. Enable “And equal to” for A ≥ B.",
    inputs: [
      { name: "a", color: "number-or-numeric" },
      { name: "b", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "boolean", label: "A > B" }],
  },
  "Fill": {
    description:
      "Replaces NaN (missing) values in a Numeric dataset with a constant replacement value.",
    inputs: [
      { name: "in", color: "numeric" },
      { name: "replace", color: "number-or-numeric" },
    ],
    outputs: [{ name: "out", color: "numeric" }],
  },

  // ---------- Properties ----------
  "Unit Property": {
    description:
      "Attaches a unit label (km, m, count, etc.) to a Map layer or Save dataset's metadata — affects how the dataset is displayed and labelled.",
    outputs: [{ name: "Unit", color: "property" }],
  },
  "Area Property": {
    description:
      "Attaches an area-unit label (km², ha, m², etc.) to a Map layer or Save dataset's metadata.",
    outputs: [{ name: "Area", color: "property" }],
  },
  "Type Property": {
    description:
      "Attaches a value-type label (e.g. 'Normal' or 'Standard Deviation') to a Map layer or Save dataset's metadata — affects how the dataset is interpreted by downstream tools.",
    outputs: [{ name: "Type", color: "property" }],
  },

  // ---------- Debug tools ----------
  "Cell area": {
    description: "Returns the per-cell ground area (in m²) of the input dataset's tile grid.",
    inputs: [{ name: "in", color: "any" }],
    outputs: [{ name: "out", color: "number", label: "m²" }],
  },
  "Rescale": {
    description:
      "Re-tiles a Boolean dataset to a different zoom level (tile-grid resolution). Useful for matching resolutions before combining datasets.",
    inputs: [
      { name: "in", color: "boolean" },
      { name: "zoom", color: "number", label: "Target zoom level" },
    ],
    outputs: [{ name: "out", color: "boolean" }],
  },
}

/**
 * Look up the info entry for a component by its registration name. Returns
 * undefined when no entry exists — callers should fall back to the
 * description-only rendering.
 */
export function getComponentInfo(name: string): ComponentInfoEntry | undefined {
  return COMPONENT_INFO[name]
}

// ---------------------------------------------------------------------------
// Dynamic output / source derivation
// ---------------------------------------------------------------------------
//
// Some components (notably RasterBoolCatComponent, RasterComponent,
// VectorComponent, DigitalModelComponent) carry their per-class / per-option
// metadata as instance state populated at construction time. The Toolbox
// info panel pulls that state out so users see the real classes (e.g. the 12
// MapBiomas land-cover classes) rather than a generic "per class" placeholder.
//
// We use duck typing here rather than `instanceof` so this module doesn't
// need to import each component class (avoids potential circular imports and
// keeps the logic robust against future class renames).

interface RBCOptionLike {
  label: string
  value: number
}

interface RBCSourceLike {
  name: string
  source: string
}

interface RBCCompPropsLike {
  options: RBCOptionLike[]
  sources: RBCSourceLike[]
  combineSources?: boolean
  sourceType?: string
  name?: string
}

interface RasterOptionLike {
  name: string
  bool?: number
  unit?: string
  area?: string
  option?: string | string[]
}

interface VectorLayerLike {
  name: string
  output: "BooleanTileGrid" | "CategoricalTileGrid" | "NumericTileGrid" | string
}

interface DigitalModelLike {
  name: string
  region?: string
  badge?: string
}

function isRBCComponent(
  component: BaseComponent
): component is BaseComponent & { compProps: RBCCompPropsLike } {
  const c = component as unknown as { compProps?: RBCCompPropsLike }
  return (
    !!c.compProps &&
    Array.isArray(c.compProps.options) &&
    Array.isArray(c.compProps.sources) &&
    c.compProps.options.every(
      (o) => o && typeof o.label === "string" && typeof o.value === "number"
    )
  )
}

function isRasterComponent(
  component: BaseComponent
): component is BaseComponent & {
  options: RasterOptionLike[]
  optionLabels?: string[]
} {
  const c = component as unknown as { options?: unknown; compProps?: unknown }
  if (c.compProps) return false
  return (
    Array.isArray(c.options) &&
    c.options.length > 0 &&
    (c.options as RasterOptionLike[]).every(
      (o) => o && typeof o.name === "string"
    )
  )
}

function isVectorComponent(
  component: BaseComponent
): component is BaseComponent & { layers: VectorLayerLike[]; groups?: string[] } {
  const c = component as unknown as { layers?: unknown }
  return (
    Array.isArray(c.layers) &&
    c.layers.length > 0 &&
    (c.layers as VectorLayerLike[]).every(
      (l) => l && typeof l.name === "string" && typeof l.output === "string"
    )
  )
}

function isDigitalModelComponent(
  component: BaseComponent
): component is BaseComponent & { modelList: DigitalModelLike[] } {
  if (component.name !== "Digital Model") return false
  const c = component as unknown as { modelList?: unknown }
  return Array.isArray(c.modelList)
}

/**
 * Map a Vector layer's output type to one of our `SocketColor` keys.
 */
function vectorOutputColor(output: string): SocketColor {
  if (output === "BooleanTileGrid") return "boolean"
  if (output === "CategoricalTileGrid") return "categorical"
  if (output === "NumericTileGrid") return "numeric"
  return "any"
}

/**
 * Derive per-option output sockets from a live component instance. Returns
 * undefined when the component isn't one of the dynamic types — callers can
 * then fall back to the static `outputs` declared on `ComponentInfoEntry`.
 *
 * For raster boolean/categorical components (e.g. MapBiomas, UKCEH, Land
 * Layout), this expands the shorthand `per class` summary into one row per
 * actual class plus the combined `All` Categorical roll-up.
 */
export function deriveOutputsForComponent(
  component: BaseComponent
): ComponentSocketInfo[] | undefined {
  if (isRBCComponent(component)) {
    const opts = component.compProps.options
    const out: ComponentSocketInfo[] = opts.map((opt) => ({
      name: opt.label,
      color: "boolean",
    }))
    out.push({
      name: "All",
      color: "categorical",
      label: "All classes combined",
    })
    return out
  }

  if (isDigitalModelComponent(component)) {
    // Digital Model only ever exposes a single `dm` output, but the dropdown
    // is the meaningful surface for users — that is rendered separately
    // (see deriveSelectableSourcesForComponent). Keep the curated single
    // socket entry by returning undefined here.
    return undefined
  }

  if (isRasterComponent(component)) {
    // Multi-dimensional rasters (e.g. SPAM = Crop × Indicator × Farming
    // Method × Year) produce a cross-product of potentially hundreds of
    // outputs at runtime, gated by dropdowns. Listing the raw options here
    // is more confusing than useful — fall back to the curated summary.
    if (component.optionLabels && component.optionLabels.length > 0) {
      return undefined
    }
    return component.options.map((opt) => {
      // Build the same unit string the canvas renders via `[[…]]` —
      // see `raster_component.ts#loadOutputs` for the source of truth.
      const unit = opt.bool
        ? undefined
        : opt.unit && opt.area
        ? `${opt.unit}/${opt.area}`
        : opt.unit
      // Some option names already include a literal `[[…]]` marker from
      // their props file (e.g. Wakehurst feature heights). Strip that out
      // so the badge isn't rendered twice.
      const parsedName = parseUnitMarker(opt.name)
      return {
        name: parsedName.text,
        color: opt.bool ? "boolean" : "numeric",
        unit: unit ?? parsedName.unit,
        unitDensity: unit ? unit.includes("/") : parsedName.density,
      }
    })
  }

  if (isVectorComponent(component)) {
    // Same dimensionality concern as RasterComponent — vector components
    // with `groups` defined render a cross-product gated by group dropdowns
    // (e.g. Disaster per District = Type × Year). Skip the raw-layer dump.
    if (component.groups && component.groups.length > 0) {
      return undefined
    }
    return component.layers.map((layer) => {
      // Layer names sometimes embed `[[unit]]` directly (e.g. Indonesia
      // Carbon Stock's "Carbon stock 2003 [[t/ha]]"). Pre-parse here so
      // the renderer can show a clean name + a badge.
      const parsed = parseUnitMarker(layer.name)
      return {
        name: parsed.text,
        color: vectorOutputColor(layer.output),
        unit: parsed.unit,
        unitDensity: parsed.density,
      }
    })
  }

  return undefined
}

/**
 * Some components have a dropdown control that drives which output is
 * actually emitted (e.g. MapBiomas's "Year" picker, Digital Model's "Model"
 * picker). Surfacing that list in the sidebar helps users understand what
 * variants exist before they drag the node onto the canvas.
 *
 * Returns `{ label, items }` so the sidebar can show a small heading
 * ("Year", "Model", …) plus a compact list of option names. Returns
 * undefined when the component has no such picker.
 */
export interface ComponentVariantList {
  /** Heading label, e.g. "Year" or "Model". */
  label: string
  /** Display names of available variants. */
  items: string[]
  /** Optional per-item region (used to filter by project permissions). */
  itemRegions?: (string | undefined)[]
  /** Optional per-item type badge (e.g. Elevation, Canopy). */
  itemBadges?: (string | undefined)[]
}

export function deriveSelectableSourcesForComponent(
  component: BaseComponent
): ComponentVariantList | undefined {
  if (isRBCComponent(component)) {
    const props = component.compProps
    if (!props.sources || props.sources.length <= 1) return undefined
    if (props.combineSources) return undefined
    return {
      label: props.sourceType ?? "Source",
      items: props.sources.map((s) => s.name),
    }
  }

  if (isDigitalModelComponent(component)) {
    if (component.modelList.length === 0) return undefined
    return {
      label: "Model",
      items: component.modelList.map((m) => m.name),
      itemRegions: component.modelList.map((m) => m.region),
      itemBadges: component.modelList.map((m) => m.badge),
    }
  }

  return undefined
}
